import { useCallback, useEffect, useMemo, useState } from 'react'
import { CheckCircle2, Clock3, Flag, Info, Pencil, RotateCcw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { ActionButton, Badge, Button } from '@/components/ui'
import { BottomSheet } from '@/components/ui/BottomSheet'
import {
  PriorityReviewRequestFields,
  type PriorityReviewRequestFilters,
} from '@/components/pedagogy/PriorityReviewRequestFields'
import {
  cancelPartnerPedagogyPriorityRequest,
  getPartnerPedagogyPriorityRequestContext,
  getPartnerPedagogyPriorityRequestOptions,
  savePartnerPedagogyPriorityRequest,
  type PartnerPedagogyPriorityRequestContext,
} from '@/services/pedagogyPriorityRequestService'

const EMPTY_FILTERS: PriorityReviewRequestFilters = {
  teacherId: '',
  studioInternalId: '',
  directionName: '',
  trainingDate: '',
  itemId: '',
  deferredActivation: false,
  availableFrom: '',
}

function formatPeriodDate(value: string | null): string {
  if (!value) return ''
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return value
  return `${day}.${month}.${year}`
}

function isRequestLockedMessage(message: string): boolean {
  const normalized = message.toLocaleLowerCase('ru-RU')
  return normalized.includes('уже обрабатывается')
    || normalized.includes('больше не редактируется')
    || normalized.includes('уже нельзя изменить')
}

function RequestInfo({
  periodStart,
  periodEnd,
  failed = false,
}: {
  periodStart: string | null | undefined
  periodEnd: string | null | undefined
  failed?: boolean
}) {
  return (
    <details className="relative shrink-0">
      <summary
        className="flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground [&::-webkit-details-marker]:hidden"
        title={failed ? 'Почему отсмотр не выполнен' : 'Как работает запрос на отсмотр'}
      >
        <Info className="h-4 w-4" />
      </summary>
      <div className="absolute right-0 z-30 mt-2 w-[300px] rounded-lg border border-border bg-card p-3 text-left text-xs leading-5 text-muted-foreground shadow-lg">
        <div className="font-medium text-foreground">
          {failed ? 'Отсмотр не выполнен' : 'Запрос на отсмотр'}
        </div>
        <div className="mt-1">
          {failed
            ? 'Посмотреть выбранную тренировку не удалось. Вы можете запросить другой отсмотр в этом периоде. Причину можно уточнить у вашего специалиста по педагогам.'
            : 'Один выполненный запрос на франчайзи за период. Сначала выбирается педагог, остальные параметры можно уточнить при желании.'}
        </div>
        {periodStart && periodEnd && (
          <div className="mt-1.5 text-[11px]">
            Период: {formatPeriodDate(periodStart)}—{formatPeriodDate(periodEnd)}.
          </div>
        )}
      </div>
    </details>
  )
}

export function PartnerPriorityReviewRequestCard({
  studioInternalId,
  partnerNumber,
  preselectedTeacherId = null,
  completedTarget = 'teacher',
  presentation = 'inline',
}: {
  studioInternalId: number
  partnerNumber: string
  preselectedTeacherId?: string | null
  completedTarget?: 'teacher' | 'review'
  presentation?: 'inline' | 'strip' | 'toolbar'
}) {
  const navigate = useNavigate()
  const [context, setContext] = useState<PartnerPedagogyPriorityRequestContext | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [filters, setFilters] = useState<PriorityReviewRequestFilters>(EMPTY_FILTERS)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setContext(await getPartnerPedagogyPriorityRequestContext(studioInternalId))
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError))
    } finally {
      setLoading(false)
    }
  }, [studioInternalId])

  useEffect(() => {
    void load()
  }, [load])

  const latest = context?.latestRequest ?? null
  const canEdit = latest?.status === 'open'
  const canCreateNew = Boolean(context?.canCreate)
  const canShowRequestUi = context?.accessKind === 'owner' || context?.accessKind === 'manager' || context?.accessKind === 'senior_teacher'
  const canOpenReviewDirectly = completedTarget === 'review' && Boolean(latest?.reviewKey)
  const hasTeacherContext = Boolean(preselectedTeacherId)
  const requestTargetsOtherTeacher = Boolean(
    hasTeacherContext
      && latest?.teacherId
      && latest.teacherId !== preselectedTeacherId
  )
  const requestTeacherName = latest?.teacherName?.trim() || 'другой педагог'
  const showTargetTeacher = Boolean(latest?.teacherName && (requestTargetsOtherTeacher || !hasTeacherContext))

  const matchingSessions = useMemo(() => {
    if (!context || !filters.teacherId) return []
    return context.sessions
      .filter(session => session.teacherId === filters.teacherId)
      .filter(session => !filters.directionName || session.directionName === filters.directionName)
      .filter(session => !filters.studioInternalId || String(session.studioInternalId) === filters.studioInternalId)
      .filter(session => !filters.trainingDate || session.trainingDate === filters.trainingDate)
      .filter(session => !filters.itemId || String(session.itemId) === filters.itemId)
  }, [context, filters])

  const openRequestSheet = async () => {
    if (!context) return

    setError(null)
    setNotice(null)
    setSheetOpen(true)
    setOptionsLoading(true)

    try {
      const options = await getPartnerPedagogyPriorityRequestOptions(studioInternalId)
      setContext(current => current ? { ...current, ...options } : current)

      if (canEdit && latest) {
        setFilters({
          teacherId: latest.teacherId ?? '',
          studioInternalId: latest.studioInternalId == null ? '' : String(latest.studioInternalId),
          directionName: latest.directionName ?? '',
          trainingDate: latest.trainingDate ?? '',
          itemId: latest.itemId == null ? '' : String(latest.itemId),
          deferredActivation: false,
          availableFrom: latest.availableFrom ?? '',
        })
      } else {
        const preferredTeacher = preselectedTeacherId && options.teachers.some(item => item.teacherId === preselectedTeacherId)
          ? preselectedTeacherId
          : ''
        setFilters({ ...EMPTY_FILTERS, teacherId: preferredTeacher })
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError))
    } finally {
      setOptionsLoading(false)
    }
  }

  const save = async () => {
    if (!context || !filters.teacherId) {
      setError('Сначала выберите педагога')
      return
    }
    if (matchingSessions.length === 0) {
      setError('Подходящих занятий за последние 7 дней нет')
      return
    }

    setBusy(true)
    setError(null)
    try {
      await savePartnerPedagogyPriorityRequest({
        studioInternalId,
        requestKey: canEdit ? latest?.requestKey : null,
        teacherId: filters.teacherId,
        directionName: filters.directionName || null,
        targetStudioInternalId: filters.studioInternalId ? Number(filters.studioInternalId) : null,
        trainingDate: filters.trainingDate || null,
        itemId: filters.itemId ? Number(filters.itemId) : null,
      })
      setSheetOpen(false)
      setNotice(canEdit ? 'Запрос изменён' : 'Запрос отправлен')
      await load()
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : String(saveError)
      if (canEdit && isRequestLockedMessage(message)) {
        setSheetOpen(false)
        await load()
        setNotice('Запрос уже взят в работу и больше не может быть изменён')
      } else {
        setError(message)
      }
    } finally {
      setBusy(false)
    }
  }

  const cancel = async () => {
    if (!canEdit || !latest) return
    if (!window.confirm('Отменить запрос на отсмотр? После отмены можно будет отправить новый запрос в этом периоде.')) return

    setBusy(true)
    setError(null)
    try {
      const cancelled = await cancelPartnerPedagogyPriorityRequest(studioInternalId, latest.requestKey)
      setSheetOpen(false)
      await load()
      setNotice(cancelled ? 'Запрос отменён' : 'Запрос уже взят в работу и больше не может быть отменён')
    } catch (cancelError) {
      const message = cancelError instanceof Error ? cancelError.message : String(cancelError)
      setSheetOpen(false)
      await load()
      setNotice(isRequestLockedMessage(message)
        ? 'Запрос уже взят в работу и больше не может быть отменён'
        : null)
      if (!isRequestLockedMessage(message)) setError(message)
    } finally {
      setBusy(false)
    }
  }

  const openCompletedReview = () => {
    if (!latest?.reviewTeacherId || latest.reviewStudioInternalId == null) return
    const teacherPath = `/partners/${partnerNumber}/studios/${latest.reviewStudioInternalId}/teachers/${latest.reviewTeacherId}`
    if (completedTarget === 'review' && latest.reviewKey) {
      navigate(`${teacherPath}/reviews/${latest.reviewKey}`)
      return
    }
    navigate(teacherPath)
  }

  // Не показываем даже каркас кнопки, пока сервер не подтвердил owner/senior-доступ.
  if (loading || !canShowRequestUi) return null

  const targetTeacher = showTargetTeacher ? (
    <span className="min-w-0 truncate text-xs text-muted-foreground" title={requestTeacherName}>
      {requestTeacherName}
    </span>
  ) : null

  const info = (
    <RequestInfo
      periodStart={context?.periodStart}
      periodEnd={context?.periodEnd}
      failed={latest?.status === 'failed'}
    />
  )

  const toolbarActionClass = presentation === 'toolbar' ? 'h-10 rounded-lg px-4 text-sm' : undefined

  const requestActions = latest?.status === 'done' ? (
    <>
      <Badge variant="success" className="shrink-0 whitespace-nowrap text-[11px]">
        <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
        Отсмотр готов
      </Badge>
      {targetTeacher}
      {latest.reviewTeacherId && latest.reviewStudioInternalId != null && (
        <ActionButton tone="primary" className={toolbarActionClass} onClick={openCompletedReview}>
          {requestTargetsOtherTeacher ? 'Открыть' : canOpenReviewDirectly ? 'Открыть отсмотр' : 'Открыть педагога'}
        </ActionButton>
      )}
    </>
  ) : latest?.status === 'open' || latest?.status === 'assigned' ? (
    <>
      <Badge variant="warning" className="shrink-0 whitespace-nowrap text-[11px]">
        <Clock3 className="mr-1 inline h-3.5 w-3.5" />
        Запрос отправлен
      </Badge>
      {targetTeacher}
      {canEdit && (
        <ActionButton className={toolbarActionClass} onClick={() => void openRequestSheet()} icon={<Pencil className="h-3.5 w-3.5" />}>
          Изменить
        </ActionButton>
      )}
    </>
  ) : latest?.status === 'failed' ? (
    <>
      <span className="text-xs text-muted-foreground">Отсмотр не выполнен</span>
      {canCreateNew && (
        <ActionButton tone="primary" className={toolbarActionClass} onClick={() => void openRequestSheet()} icon={<RotateCcw className="h-3.5 w-3.5" />}>
          Запросить снова
        </ActionButton>
      )}
    </>
  ) : canCreateNew ? (
    <ActionButton className={toolbarActionClass} onClick={() => void openRequestSheet()} icon={<Flag className="h-4 w-4" />}>
      Запросить отсмотр
    </ActionButton>
  ) : null

  return (
    <>
      {presentation === 'strip' ? (
        <div className="flex min-h-10 flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 text-xs font-semibold text-foreground">Запрос на отсмотр</span>
            {notice && <span className="truncate text-xs font-medium text-success">{notice}</span>}
            {error && !sheetOpen && <span className="max-w-[320px] truncate text-xs text-error">{error}</span>}
          </div>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
            {requestActions}
            {info}
          </div>
        </div>
      ) : presentation === 'toolbar' ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {requestActions}
          <div className="[&_summary]:h-10 [&_summary]:w-10 [&_summary]:rounded-lg">{info}</div>
          {notice && <span className="text-xs font-medium text-success">{notice}</span>}
          {error && !sheetOpen && <span className="max-w-[260px] text-xs text-error">{error}</span>}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {requestActions}
          {info}
          {notice && <span className="text-xs font-medium text-success">{notice}</span>}
          {error && !sheetOpen && <span className="max-w-[260px] text-xs text-error">{error}</span>}
        </div>
      )}

      <BottomSheet
        isOpen={sheetOpen}
        onClose={() => !busy && !optionsLoading && setSheetOpen(false)}
        title={canEdit ? 'Изменить запрос на отсмотр' : 'Запросить отсмотр'}
        className="sm:max-w-xl"
      >
        <div className="space-y-4 px-4 pb-5 pt-3 sm:px-5">
          <div className="text-sm leading-5 text-muted-foreground">
            Выберите педагога. Остальные параметры необязательны и берутся только из реальных занятий последних 7 дней.
          </div>

          {optionsLoading ? (
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-3 text-sm text-muted-foreground">
              Загружаем доступные тренировки…
            </div>
          ) : context && (
            <PriorityReviewRequestFields
              mode="partner"
              sessions={context.sessions}
              teacherOptions={context.teachers}
              value={filters}
              onChange={setFilters}
            />
          )}

          {error && <div className="rounded-lg border border-error/30 bg-error/5 px-3 py-2 text-sm text-error">{error}</div>}

          <div className="flex flex-col-reverse gap-2 border-t border-border pt-3 sm:flex-row sm:justify-end">
            {canEdit && latest && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="whitespace-nowrap border-error/40 text-error hover:bg-error/5"
                disabled={busy || optionsLoading}
                onClick={() => void cancel()}
              >
                Отменить запрос
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              className="whitespace-nowrap"
              disabled={busy || optionsLoading || !filters.teacherId || matchingSessions.length === 0}
              onClick={() => void save()}
            >
              {busy ? 'Сохраняем…' : canEdit ? 'Сохранить изменения' : 'Отправить запрос'}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </>
  )
}
